import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  alpha,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { FilterAltOff as ClearFilterIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  SwapHoriz as TransferIcon,
  Store as StoreIcon,
  Publish as PostIcon,
  Undo as UndoIcon,
  Cancel as UnapproveIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  CallReceived as ReceiveIcon,
  RemoveCircle as UnreceiveIcon,
} from '@mui/icons-material';
import { Print as PrintIcon, FileDownload as ExcelIcon } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';
import BranchTransferDialog from '../components/branchTransfers/BranchTransferDialog';
import BranchTransferPreviewDialog from '../components/branchTransfers/BranchTransferPreviewDialog';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { BranchTransfer } from '../types/branchTransfers.types';
import { branchTransfersService } from '../services/branchTransfers.service';

const translations = {
  en: {
    title: 'Branch Transfers',
    addBranchTransfer: 'Add Branch Transfer',
    transferNumber: 'Transfer Number',
    transferDate: 'Transfer Date',
    fromBranch: 'From Branch',
    toBranch: 'To Branch',
    itemsCount: 'Items',
    status: 'Status',
    actions: 'Actions',
    search: 'Search by transfer number...',
    noData: 'No branch transfers found',
    confirmDelete: 'Are you sure you want to delete this branch transfer?',
    confirmApprove: 'Are you sure you want to approve this transfer? Stock will be deducted from the source branch.',
    confirmReceive: 'Are you sure you want to confirm receipt? Stock will be added to your branch.',
    confirmUnapprove: 'Are you sure you want to unapprove this transfer? Source stock will be restored.',
    confirmUnreceive: 'Are you sure you want to cancel receipt? Destination stock will be reversed.',
    confirmPost: 'Are you sure you want to post this transfer? Journal entry will be recorded in the general ledger.',
    confirmUnpost: 'Are you sure you want to unpost this transfer? GL entries will be removed.',
    draft: 'Draft',
    approved: 'Approved',
    received: 'Received',
    posted: 'Posted',
    approve: 'Approve',
    receive: 'Confirm Receipt',
    unreceive: 'Cancel Receipt',
    unapprove: 'Unapprove',
    post: 'Post',
    unpost: 'Unpost',
    receivedDate: 'Received Date',
    editTransfer: 'Edit Transfer',
    deleteTransfer: 'Delete Transfer',
    viewTransfer: 'View Transfer',
    approvedDate: 'Approved',
    postedDate: 'Posted',
    journalEntry: 'Journal Entry',
    noJournalEntry: 'No Journal Entry (inventory accounts not configured)',
    printList: 'Print List',
    exportExcel: 'Export Excel',
    preview: 'Preview',
    notes: 'Notes',
    total: 'Total',
  },
  ar: {
    title: 'تحويلات الفروع',
    addBranchTransfer: 'إضافة تحويل بين فروع',
    transferNumber: 'رقم التحويل',
    transferDate: 'تاريخ التحويل',
    fromBranch: 'من الفرع',
    toBranch: 'إلى الفرع',
    itemsCount: 'الأصناف',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'البحث برقم التحويل...',
    noData: 'لا توجد تحويلات فروع',
    confirmDelete: 'هل أنت متأكد من حذف التحويل؟',
    confirmApprove: 'هل أنت متأكد من اعتماد التحويل؟ سيتم خصم المخزون من الفرع المرسل.',
    confirmReceive: 'هل أنت متأكد من تأكيد الاستلام؟ سيتم إضافة المخزون لفرعك.',
    confirmUnapprove: 'هل أنت متأكد من إلغاء الاعتماد؟ سيتم استعادة مخزون الفرع المرسل.',
    confirmUnreceive: 'هل أنت متأكد من إلغاء الاستلام؟ سيتم عكس مخزون الفرع المستقبل.',
    confirmPost: 'هل أنت متأكد من ترحيل التحويل؟ سيتم تسجيل القيد في دفتر الأستاذ العام.',
    confirmUnpost: 'هل أنت متأكد من إلغاء الترحيل؟ سيتم حذف قيود دفتر الأستاذ.',
    draft: 'مسودة',
    approved: 'معتمد',
    received: 'مستلم',
    posted: 'مرحّل',
    approve: 'اعتماد',
    receive: 'تأكيد الاستلام',
    unreceive: 'إلغاء الاستلام',
    unapprove: 'إلغاء الاعتماد',
    post: 'ترحيل',
    unpost: 'إلغاء الترحيل',
    receivedDate: 'تاريخ الاستلام',
    editTransfer: 'تعديل التحويل',
    deleteTransfer: 'حذف التحويل',
    viewTransfer: 'عرض التحويل',
    approvedDate: 'تاريخ الاعتماد',
    postedDate: 'تاريخ الترحيل',
    journalEntry: 'القيد المحاسبي',
    noJournalEntry: 'لا يوجد قيد (حسابات المخزون غير مضبوطة)',
    printList: 'طباعة القائمة',
    exportExcel: 'تصدير إكسل',
    preview: 'معاينة',
    notes: 'ملاحظات',
    total: 'الإجمالي',
  },
};

const BranchTransfersPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const { userBranchIds, userRoles } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userRoles: [] as string[] };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
        ? (Array.isArray(payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'])
            ? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
            : [payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']])
        : [];
      return {
        userBranchIds: Array.from(new Set([...primaryBranch, ...allowedBranches])),
        userRoles: roles as string[]
      };
    } catch {
      return { userBranchIds: [] as number[], userRoles: [] as string[] };
    }
  })();

  const approverRoles = ['Super Admin', 'Admin', 'Accountant', 'Senior Accountant', 'Chief Accountant'];
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();
  
  const canViewBranchTransfer = (transfer: BranchTransfer) => {
    if (userBranchIds.length === 0) return true;
    return userBranchIds.includes(transfer.fromBranchId) || userBranchIds.includes(transfer.toBranchId);
  };
  const canEditBranchTransfer = (transfer: BranchTransfer) => {
    if (userBranchIds.length === 0) return true; // Admin
    // Only the SENDER branch can edit
    return userBranchIds.includes(transfer.fromBranchId);
  };
  const isReceiverOnly = (transfer: BranchTransfer) => {
    if (userBranchIds.length === 0) return false; // Admin sees all actions
    return userBranchIds.includes(transfer.toBranchId) && !userBranchIds.includes(transfer.fromBranchId);
  };
  const canApproveBranchTransfer = () => {
    return userRoles.some(r => approverRoles.includes(r));
  };

  const [branchTransfers, setBranchTransfers] = useState<BranchTransfer[]>([]);
  const [filteredBranchTransfers, setFilteredBranchTransfers] = useState<BranchTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { minDate, maxDate } = useFiscalDateRange();
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTransfer, setPreviewTransfer] = useState<BranchTransfer | null>(null);
  const [selectedBranchTransfer, setSelectedBranchTransfer] = useState<BranchTransfer | undefined>();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'delete' | 'approve' | 'receive' | 'unreceive' | 'unapprove' | 'post' | 'unpost' | null;
    transferId: number | null;
    transferNumber: string;
  }>({ open: false, action: null, transferId: null, transferNumber: '' });

  const confirmConfig = {
    delete:      { color: '#ef4444', icon: <DeleteIcon sx={{ fontSize: 48 }} />, confirmColor: 'error'     as const },
    approve:     { color: '#f59e0b', icon: <ApproveIcon sx={{ fontSize: 48 }} />, confirmColor: 'warning'   as const },
    receive:     { color: '#0ea5e9', icon: <ReceiveIcon sx={{ fontSize: 48 }} />, confirmColor: 'info'      as const },
    unreceive:   { color: '#f97316', icon: <UnreceiveIcon sx={{ fontSize: 48 }} />, confirmColor: 'warning' as const },
    unapprove:   { color: '#f59e0b', icon: <UnapproveIcon sx={{ fontSize: 48 }} />, confirmColor: 'warning' as const },
    post:        { color: '#10b981', icon: <PostIcon sx={{ fontSize: 48 }} />, confirmColor: 'success'     as const },
    unpost:      { color: '#6366f1', icon: <UndoIcon sx={{ fontSize: 48 }} />, confirmColor: 'secondary'   as const },
  };

  const openConfirm = (action: typeof confirmDialog['action'], transferId: number, transferNumber: string) => {
    setConfirmDialog({ open: true, action, transferId, transferNumber });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, action: null, transferId: null, transferNumber: '' });
  };

  const handleConfirm = async () => {
    const { action, transferId } = confirmDialog;
    if (!action || !transferId) return;
    closeConfirm();
    try {
      if (action === 'delete')      await branchTransfersService.delete(transferId);
      if (action === 'approve')     await branchTransfersService.approve(transferId);
      if (action === 'receive')     await branchTransfersService.receive(transferId);
      if (action === 'unreceive')   await branchTransfersService.unreceive(transferId);
      if (action === 'unapprove')   await branchTransfersService.unapprove(transferId);
      if (action === 'post')        await branchTransfersService.post(transferId);
      if (action === 'unpost')      await branchTransfersService.unpost(transferId);
      loadBranchTransfers();
      toast.success(isRTL ? 'تمت العملية بنجاح' : 'Operation completed successfully');
    } catch (error: any) {
      console.error(`Failed to ${action}:`, error);
      toast.error(error.response?.data?.message || (isRTL ? 'فشلت العملية' : `Failed to ${action} transfer`));
    }
  };

  useEffect(() => {
    loadBranchTransfers();
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  // ✅ Auto-open transfer preview when ?openId=X is in the URL (used by Data Integrity report)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('openId');
    if (!openId) return;
    const id = parseInt(openId, 10);
    if (!Number.isFinite(id)) return;
    (async () => {
      try {
        const full = await branchTransfersService.getById(id);
        setPreviewTransfer(full);
        setPreviewDialogOpen(true);
      } catch {
        toast.error('Failed to open transfer');
      }
    })();
  }, []);

  const statuses = useMemo(() => Array.from(new Set(branchTransfers.map(bt => bt.status).filter(Boolean))), [branchTransfers]);

  useEffect(() => {
    filterBranchTransfers();
  }, [searchTerm, filterStatus, filterDateFrom, filterDateTo, branchTransfers]);

  const loadBranchTransfers = async () => {
    setLoading(true);
    try {
      const data = await branchTransfersService.getAll();
      const filteredData = data
        .filter((transfer: BranchTransfer) => canViewBranchTransfer(transfer))
        .sort((a: BranchTransfer, b: BranchTransfer) => b.branchTransferId - a.branchTransferId);
      setBranchTransfers(filteredData);
    } catch (error) {
      console.error('Failed to load branch transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBranchTransfers = () => {
    let result = branchTransfers;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(bt => bt.transferNumber.toLowerCase().includes(term));
    }
    if (filterStatus) result = result.filter(bt => bt.status === filterStatus);
    if (filterDateFrom) result = result.filter(bt => bt.transferDate >= filterDateFrom);
    if (filterDateTo) result = result.filter(bt => bt.transferDate <= filterDateTo);

    setFilteredBranchTransfers(result);
  };

  const handleAdd = () => {
    setSelectedBranchTransfer(undefined);
    setDialogOpen(true);
  };

  const handleView = async (branchTransfer: BranchTransfer) => {
    try {
      const fullTransfer = await branchTransfersService.getById(branchTransfer.branchTransferId);
      setPreviewTransfer(fullTransfer);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error(isRTL ? 'فشل تحميل تفاصيل التحويل' : 'Failed to load transfer details');
    }
  };

  const handleEdit = async (branchTransfer: BranchTransfer) => {
    try {
      // Load full transfer with details
      const fullTransfer = await branchTransfersService.getById(branchTransfer.branchTransferId);
      setSelectedBranchTransfer(fullTransfer);
      setDialogOpen(true);
    } catch (error) {
      console.error('❌ Failed to load transfer details:', error);
      toast.error(isRTL ? 'فشل تحميل تفاصيل التحويل' : 'Failed to load transfer details');
    }
  };


  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedBranchTransfer(undefined);
  };

  const handleSave = () => {
    loadBranchTransfers();
  };

  const getStatusColor = (status?: string): 'default' | 'warning' | 'success' | 'primary' | 'info' => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Approved': return 'warning';
      case 'Received': return 'info';
      case 'Posted': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Draft': return t.draft;
      case 'Approved': return t.approved;
      case 'Received': return t.received;
      case 'Posted': return t.posted;
      default: return status || '-';
    }
  };

  const canReceiveBranchTransfer = (transfer: BranchTransfer) => {
    return userBranchIds.includes(transfer.toBranchId) || userBranchIds.length === 0;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const handlePrintList = () => {
    const rows = filteredBranchTransfers.map((bt, i) => `
      <tr>
        <td style="text-align:center">${i + 1}</td>
        <td style="font-weight:bold;color:#1976d2">${bt.transferNumber}</td>
        <td>${formatDate(bt.transferDate)}</td>
        <td>${isRTL ? bt.fromBranchNameAr : bt.fromBranchName}</td>
        <td>${isRTL ? bt.toBranchNameAr : bt.toBranchName}</td>
        <td style="text-align:center">${bt.details?.length || 0}</td>
        <td><span style="padding:2px 8px;border-radius:10px;font-size:11px;color:white;background:${bt.status === 'Posted' ? '#2e7d32' : bt.status === 'Approved' ? '#ed6c02' : '#9e9e9e'}">${getStatusLabel(bt.status)}</span></td>
        <td>${bt.notes || '-'}</td>
      </tr>
    `).join('');
    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><style>
      body{font-family:${isRTL ? 'Cairo' : 'Arial'},sans-serif;margin:20px;font-size:12px;color:#333;}
      .header{text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;}
      .header h2{color:#1976d2;margin:5px 0;} .header p{font-size:11px;color:#666;margin:0;}
      h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
      table{width:100%;border-collapse:collapse;margin-top:10px;}
      th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
      th{background:#1976d2;color:white;font-weight:bold;}
      tr:nth-child(even){background:#f9f9f9;}
      @page{size:A4 landscape;margin:1cm;}
    </style></head><body>
      <div class="header">
        ${companyInfo?.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2>${isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</h2>
        <p>${companyInfo?.address ? companyInfo.address + ' | ' : ''}${companyInfo?.phone ? companyInfo.phone + ' | ' : ''}${companyInfo?.email || ''}</p>
      </div>
      <h1>${t.title}</h1>
      <table><thead><tr>
        <th>#</th><th>${t.transferNumber}</th><th>${t.transferDate}</th>
        <th>${t.fromBranch}</th><th>${t.toBranch}</th><th>${t.itemsCount}</th>
        <th>${t.status}</th><th>${t.notes}</th>
      </tr></thead><tbody>${rows}</tbody>
      <tfoot><tr style="background:#e3f2fd;font-weight:bold"><td colspan="5" style="text-align:${isRTL ? 'left' : 'right'}">${t.total}</td><td style="text-align:center">${filteredBranchTransfers.length}</td><td colspan="2"></td></tr></tfoot>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=1000,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);
      worksheet.getRow(1).height = 50;
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }
      const headerRow = worksheet.getRow(1);
      headerRow.getCell(3).value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      headerRow.getCell(3).font = { size: 16, bold: true, color: { argb: 'FF1976D2' } };
      worksheet.mergeCells('C1:H1');

      const titleRow = worksheet.getRow(3);
      titleRow.getCell(1).value = t.title;
      titleRow.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      worksheet.mergeCells('A3:H3');

      const headers = ['#', t.transferNumber, t.transferDate, t.fromBranch, t.toBranch, t.itemsCount, t.status, t.notes];
      const hRow = worksheet.getRow(5);
      headers.forEach((h, i) => {
        const cell = hRow.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
      });

      filteredBranchTransfers.forEach((bt, i) => {
        const row = worksheet.getRow(6 + i);
        row.getCell(1).value = i + 1;
        row.getCell(2).value = bt.transferNumber;
        row.getCell(3).value = formatDate(bt.transferDate);
        row.getCell(4).value = isRTL ? bt.fromBranchNameAr : bt.fromBranchName;
        row.getCell(5).value = isRTL ? bt.toBranchNameAr : bt.toBranchName;
        row.getCell(6).value = bt.details?.length || 0;
        row.getCell(7).value = getStatusLabel(bt.status);
        row.getCell(8).value = bt.notes || '';
        if (i % 2 === 1) row.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }; });
        row.eachCell(c => { c.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }; });
      });

      worksheet.columns = [{ width: 6 }, { width: 18 }, { width: 14 }, { width: 20 }, { width: 20 }, { width: 10 }, { width: 12 }, { width: 25 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BranchTransfers_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={t.printList}>
            <IconButton onClick={handlePrintList} color="primary" disabled={filteredBranchTransfers.length === 0}><PrintIcon /></IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton onClick={exportToExcel} sx={{ color: '#2e7d32' }} disabled={filteredBranchTransfers.length === 0}><ExcelIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadBranchTransfers} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            {t.addBranchTransfer}
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 3,
          background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
          borderRadius: 2,
        }}
      >
        <TextField
          fullWidth
          placeholder={t.search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'transparent',
              },
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150, backgroundColor: 'background.paper', borderRadius: 1 }}>
            <InputLabel>{isRTL ? 'الحالة' : 'Status'}</InputLabel>
            <Select value={filterStatus} label={isRTL ? 'الحالة' : 'Status'} onChange={(e) => setFilterStatus(e.target.value as string)}>
              <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
              {statuses.map(s => <MenuItem key={s} value={s}>{getStatusLabel(s)}</MenuItem>)}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={isRTL ? 'من تاريخ' : 'From'}
              value={filterDateFrom ? new Date(filterDateFrom) : null}
              onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateFrom(v); }}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={filterDateTo ? new Date(filterDateTo) : maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160, backgroundColor: 'background.paper', borderRadius: 1 } } }}
            />
            <DatePicker
              label={isRTL ? 'إلى تاريخ' : 'To'}
              value={filterDateTo ? new Date(filterDateTo) : null}
              onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateTo(v); }}
              format="dd/MM/yyyy"
              minDate={filterDateFrom ? new Date(filterDateFrom) : minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160, backgroundColor: 'background.paper', borderRadius: 1 } } }}
            />
          </LocalizationProvider>
          {(filterStatus || filterDateFrom || filterDateTo) && (
            <Button size="small" startIcon={<ClearFilterIcon />} onClick={() => { setFilterStatus(''); setFilterDateFrom(''); setFilterDateTo(''); }} sx={{ color: 'white' }}>
              {isRTL ? 'مسح' : 'Clear'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Transfers List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredBranchTransfers.map((bt) => (
            <Card
              key={bt.branchTransferId}
              elevation={2}
              sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
                border: '1px solid',
                borderColor: bt.status === 'Posted' ? 'success.light' : bt.status === 'Received' ? 'info.light' : bt.status === 'Approved' ? 'warning.light' : 'grey.300',
                borderLeft: 6,
                borderLeftColor: bt.status === 'Posted' ? 'success.main' : bt.status === 'Received' ? 'info.main' : bt.status === 'Approved' ? 'warning.main' : 'grey.400',
              }}
            >
              <CardContent>
                {/* Top Row */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 2,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TransferIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                        {bt.transferNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(bt.transferDate)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                      label={getStatusLabel(bt.status)}
                      color={getStatusColor(bt.status)}
                      sx={{ fontWeight: 600, minWidth: 100 }}
                    />
                    <Chip
                      label={`${bt.details.length} ${t.itemsCount}`}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Branches Info */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 3,
                  mb: 2,
                }}>
                  {/* From/To Branches */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t.fromBranch}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <StoreIcon color="error" fontSize="small" />
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {isRTL ? bt.fromBranchNameAr : bt.fromBranchName}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t.toBranch}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StoreIcon color="success" fontSize="small" />
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {isRTL ? bt.toBranchNameAr : bt.toBranchName}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Approved Date */}
                  {(bt.status === 'Approved' || bt.status === 'Posted') && bt.approvedDate && (
                    <Box sx={{
                      flex: 0.5,
                      p: 2,
                      bgcolor: alpha('#ff9800', 0.1),
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha('#ff9800', 0.3),
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ApproveIcon color="warning" />
                        <Typography variant="caption" color="text.secondary">
                          {t.approvedDate}
                        </Typography>
                      </Box>
                      <Typography variant="body1" color="warning.main" sx={{ fontWeight: 600 }}>
                        {formatDate(bt.approvedDate)}
                      </Typography>
                    </Box>
                  )}

                  {bt.status === 'Posted' && bt.postedDate && (
                    <Box sx={{
                      flex: 0.5,
                      p: 2,
                      bgcolor: alpha('#4caf50', 0.1),
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha('#4caf50', 0.3),
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PostIcon color="success" />
                        <Typography variant="caption" color="text.secondary">
                          {t.postedDate}
                        </Typography>
                      </Box>
                      <Typography variant="body1" component="div" color="success.main" sx={{ fontWeight: 600 }}>
                        {formatDate(bt.postedDate)}
                      </Typography>
                      {bt.journalEntryId && (
                        <Chip
                          icon={<ReceiptIcon />}
                          label={`${t.journalEntry} ${bt.journalEntryNumber || 'JE'} (#${bt.journalEntryId})`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Action Buttons */}
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                }}>
                  <Tooltip title={isRTL ? 'معاينة وطباعة' : 'Preview & Print'}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      startIcon={<ViewIcon />}
                      onClick={() => handleView(bt)}
                    >
                      {isRTL ? 'معاينة' : 'Preview'}
                    </Button>
                  </Tooltip>
                  {bt.status === 'Draft' && !isReceiverOnly(bt) && (
                    <>
                      {canEditBranchTransfer(bt) && (
                        <>
                          <Tooltip title={t.editTransfer}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleEdit(bt)}
                            >
                              {t.editTransfer}
                            </Button>
                          </Tooltip>
                          <Tooltip title={t.deleteTransfer}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => openConfirm('delete', bt.branchTransferId, bt.transferNumber)}
                            >
                              {t.deleteTransfer}
                            </Button>
                          </Tooltip>
                        </>
                      )}
                      {canApproveBranchTransfer() && (
                        <Tooltip title={t.confirmApprove}>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            startIcon={<ApproveIcon />}
                            onClick={() => openConfirm('approve', bt.branchTransferId, bt.transferNumber)}
                          >
                            {t.approve}
                          </Button>
                        </Tooltip>
                      )}
                    </>
                  )}

                  {bt.status === 'Approved' && (
                    <>
                      {canReceiveBranchTransfer(bt) && (
                        <Tooltip title={t.confirmReceive}>
                          <Button
                            size="small"
                            variant="contained"
                            color="info"
                            startIcon={<ReceiveIcon />}
                            onClick={() => openConfirm('receive', bt.branchTransferId, bt.transferNumber)}
                          >
                            {t.receive}
                          </Button>
                        </Tooltip>
                      )}
                      {canApproveBranchTransfer() && (
                        <Tooltip title={t.confirmUnapprove}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<UnapproveIcon />}
                            onClick={() => openConfirm('unapprove', bt.branchTransferId, bt.transferNumber)}
                          >
                            {t.unapprove}
                          </Button>
                        </Tooltip>
                      )}
                    </>
                  )}

                  {bt.status === 'Received' && (
                    <>
                      {canPost && (
                          <Tooltip title={t.confirmPost}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<PostIcon />}
                              onClick={() => openConfirm('post', bt.branchTransferId, bt.transferNumber)}
                            >
                              {t.post}
                            </Button>
                          </Tooltip>
                      )}
                      {canApproveBranchTransfer() && (
                          <Tooltip title={t.confirmUnreceive}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<UnreceiveIcon />}
                              onClick={() => openConfirm('unreceive', bt.branchTransferId, bt.transferNumber)}
                            >
                              {t.unreceive}
                            </Button>
                          </Tooltip>
                      )}
                    </>
                  )}

                  {bt.status === 'Posted' && (
                    <>
                      {canPost && (
                        <Tooltip title={t.confirmUnpost}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<UndoIcon />}
                            onClick={() => openConfirm('unpost', bt.branchTransferId, bt.transferNumber)}
                          >
                            {t.unpost}
                          </Button>
                        </Tooltip>
                      )}
                      <Chip
                        icon={<ApproveIcon />}
                        label={t.posted}
                        color="success"
                        sx={{ fontWeight: 600, px: 1, py: 2.5 }}
                      />
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}

          {filteredBranchTransfers.length === 0 && (
            <Paper
              sx={{
                p: 8,
                textAlign: 'center',
                bgcolor: alpha('#1976d2', 0.02),
              }}
            >
              <TransferIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {t.noData}
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      <BranchTransferDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        branchTransfer={selectedBranchTransfer}
        onSave={handleSave}
      />

      <BranchTransferPreviewDialog
        open={previewDialogOpen}
        onClose={() => { setPreviewDialogOpen(false); setPreviewTransfer(null); }}
        transfer={previewTransfer}
        onEdit={() => {
          setPreviewDialogOpen(false);
          if (previewTransfer) {
            setSelectedBranchTransfer(previewTransfer);
            setDialogOpen(true);
          }
        }}
      />

      {/* Confirmation Dialog */}
      {confirmDialog.action && (
        <Dialog
          open={confirmDialog.open}
          onClose={closeConfirm}
          maxWidth="xs"
          fullWidth
          dir={isRTL ? 'rtl' : 'ltr'}
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box sx={{
            background: `linear-gradient(135deg, ${confirmConfig[confirmDialog.action].color} 0%, ${confirmConfig[confirmDialog.action].color}cc 100%)`,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: 'white',
            gap: 1,
          }}>
            {confirmConfig[confirmDialog.action].icon}
            <Typography variant="h6" fontWeight={700} textAlign="center">
              {confirmDialog.transferNumber}
            </Typography>
          </Box>

          <DialogContent sx={{ pt: 3, pb: 1, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <WarningIcon sx={{ color: 'warning.main' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {isRTL ? 'تأكيد العملية' : 'Confirm Action'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {confirmDialog.action === 'delete'      && t.confirmDelete}
              {confirmDialog.action === 'approve'     && t.confirmApprove}
              {confirmDialog.action === 'receive'     && t.confirmReceive}
              {confirmDialog.action === 'unreceive'   && t.confirmUnreceive}
              {confirmDialog.action === 'unapprove'   && t.confirmUnapprove}
              {confirmDialog.action === 'post'        && t.confirmPost}
              {confirmDialog.action === 'unpost'      && t.confirmUnpost}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button onClick={closeConfirm} variant="outlined" fullWidth sx={{ borderRadius: 2 }}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color={confirmConfig[confirmDialog.action].confirmColor}
              fullWidth
              sx={{ borderRadius: 2 }}
            >
              {isRTL ? 'تأكيد' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default BranchTransfersPage;